import httpClient from "@/config/axios";
import { useInfiniteQuery } from "@tanstack/react-query";

export interface AvailableMember {
  account_id: string;
  big_kind_name: string;
  birth_date?: string | null;
  career: string | null;
  duty: string | null;
  emp_num: string | null;
  gender?: string | null;
  is_head_nurse: string | null;
  joining_date?: string | null;
  mb_part_name: string;
  middle_kind_name: string;
  name: string;
  nurse_id: string;
  phone_number: string;
  small_kind_name: string;
  // 타 병동 등록 여부(선택 불가 표시용). 백엔드 additive 필드.
  is_registered?: boolean;
  registered_group_id?: string | null;
  registered_group_name?: string | null;
}

const DEFAULT_AVAILABLE_MEMBER_LIMIT = 20;

export type AvailableMemberSearchBy = "name" | "affiliation";

export interface AvailableMemberPage {
  items: AvailableMember[];
  next_cursor: string | null;
  has_next: boolean;
}

type AvailableMemberApiResponse = AvailableMember[] | AvailableMemberPage;

function normalizeAvailableMemberPage(
  response: AvailableMemberApiResponse,
  limit: number
): AvailableMemberPage {
  if (Array.isArray(response)) {
    const items = response.filter(
      (member): member is AvailableMember =>
        Boolean(member && typeof member.nurse_id === "string")
    );

    return {
      items: items.slice(0, limit),
      next_cursor: null,
      has_next: false,
    };
  }

  const items = Array.isArray(response.items)
    ? response.items.filter(
        (member): member is AvailableMember =>
          Boolean(member && typeof member.nurse_id === "string")
      )
    : [];

  return {
    items: items.slice(0, limit),
    next_cursor: response.next_cursor ?? null,
    has_next: Boolean(response.has_next),
  };
}

/**
 * 이거 쓰시면돼요
 * 이 엔드포인트는 수간호사라도 Group Id 넘겨야해요
 */
export default function useAvailableMember({
  groupId,
  searchName = "",
  searchBy = "name",
  limit = DEFAULT_AVAILABLE_MEMBER_LIMIT,
}: {
  groupId?: string | null;
  searchName?: string;
  searchBy?: AvailableMemberSearchBy;
  limit?: number;
}) {
  const normalizedSearchName = searchName.trim();

  return useInfiniteQuery<AvailableMemberPage>({
    queryKey: [
      "AVAILABLE-MEMBER",
      groupId ?? "",
      normalizedSearchName,
      searchBy,
      limit,
    ],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const cursorParam =
        typeof pageParam === "string" && pageParam ? pageParam : undefined;

      return await httpClient.get<AvailableMemberApiResponse>(
        "/nurses/available-members",
        {
          params: {
            ...(groupId ? { group_id: groupId } : {}),
            pagination: "cursor",
            limit,
            ...(cursorParam ? { cursor: cursorParam } : {}),
            ...(normalizedSearchName ? { q: normalizedSearchName } : {}),
            search_by: searchBy,
          },
        }
      ).then((response) => normalizeAvailableMemberPage(response, limit));
    },
    getNextPageParam: (lastPage) =>
      lastPage.has_next && lastPage.next_cursor
        ? lastPage.next_cursor
        : undefined,
    enabled: !!groupId,
    retryOnMount: false,
  });
}
